import {
  getRetailerCheckoutOrderStatusV2,
  reSubmitCheckout,
  RetailerCheckoutOrderStatusV2,
  submitUserInteraction,
} from "@/api/rest/checkoutApi";
import {
  FastCheckoutMachineContext,
  FastCheckoutMachineEvents,
  UserRetailerCheckoutCommunicationResponse,
} from "@/components/Checkout/CheckoutMachine/types/FastCheckoutMachine";
import {
  UserInteractionType,
  UserRetailerCheckoutOrder,
} from "@/components/Checkout/types";

import { useEffect, useRef, useState } from "react";
import { assign, createActor, createMachine, fromPromise } from "xstate";

const retryCheckoutActor = fromPromise(
  async ({
    input,
  }: {
    input: {
      context: FastCheckoutMachineContext;
      event: FastCheckoutMachineEvents;
    };
  }) => {
    const { orderId } = input.context;
    if (!orderId) {
      throw new Error("No order ID provided for polling");
    }

    const res = await reSubmitCheckout({ orderId });
    return res;
  }
);

// API function to track order status
const fetchOrderStatus = async (
  orderId: string
): Promise<RetailerCheckoutOrderStatusV2> => {
  try {
    // Replace with your actual API endpoint
    const response = await getRetailerCheckoutOrderStatusV2({ orderId });

    if (!response.status || response.status !== "success") {
      throw new Error(`HTTP ${response.status}: ${response}`);
    }

    return response;
  } catch (error: any) {
    throw new Error(`Failed to fetch order status: ${error?.message}`);
  }
};

// API function to submit user interaction
const submitUserInteractionApi = fromPromise(
  async ({
    input,
  }: {
    input: {
      orderId: string;
      communicationId: string;
      interactionData: any;
    };
  }) => {
    const { orderId, communicationId, interactionData } = input;

    if (!orderId || !communicationId) {
      throw new Error("No order ID or communication ID provided");
    }

    const res = await submitUserInteraction(orderId, communicationId, {
      response: interactionData,
    });
    return res;
  }
);

// XState machine for order tracking
const createOrderTrackingMachine = (orderId: string) =>
  createMachine({
    id: "orderTracking",
    initial: "idle",
    context: {
      orderId,
      orderData: undefined as UserRetailerCheckoutOrder | undefined,
      error: null as Error | null,
      retryCount: 0,
    },
    on: {
      SUBMIT_USER_INTERACTION: {
        target: ".submittingInteraction",
      },
      SUBMIT_USER_INTERACTION_AND_RETRY: {
        target: ".timeoutRetry",
      },
      RETRY: {
        target: ".timeoutRetry.retrying",
      },
      STOP_TRACKING: ".idle",
      REFRESH: ".fetching",
    },
    states: {
      idle: {
        on: {
          START_TRACKING: "waiting",
        },
      },
      waiting: {
        after: {
          0: "fetching",
        },
        on: {
          STOP_TRACKING: "idle",
          FETCH_NOW: "fetching",
        },
      },
      fetching: {
        invoke: {
          id: "fetchOrderStatus",
          src: fromPromise(async () => {
            return fetchOrderStatus(orderId);
          }),
          onDone: [
            {
              target: "completed",
              guard: ({ event }) => {
                const orderData = event.output as any;
                return orderData?.showConnectPage !== true;
              },
              actions: assign({
                orderData: ({ event }) => event.output,
                error: null,
                retryCount: 0,
              }),
            },
            {
              // Check if user interaction is required
              target: "userInteractionRequired",
              guard: ({ event }) => {
                const orderData = event.output as any;
                return !!orderData?.userRetailerCheckoutOrder
                  ?.userInteractionRequirement?.type;
              },
              actions: assign({
                orderData: ({ event }) => event.output,
                error: null,
                retryCount: 0,
              }),
            },
            {
              target: "success",
              actions: assign({
                orderData: ({ event }) => event.output,
                error: null,
                retryCount: 0,
              }),
            },
          ],
          onError: {
            target: "error",
            actions: assign({
              error: ({ event }) => event.error as Error,
              retryCount: ({ context }) => context.retryCount + 1,
            }),
          },
        },
      },
      success: {
        after: {
          3000: {
            target: "fetching",
            guard: ({ context }) => {
              const orderData = context.orderData as any;
              return orderData?.showConnectPage === true;
            },
          },
        },
        on: {
          STOP_TRACKING: "idle",
          REFRESH: "fetching",
        },
      },
      userInteractionRequired: {
        after: {
          // Continue polling to check if interaction is still required
          3000: {
            target: "fetching",
          },
        },
      },
      timeoutRetry: {
        initial: "submittingInteraction",
        states: {
          submittingInteraction: {
            invoke: {
              id: "submitUserInteractionForTimeout",
              src: submitUserInteractionApi,
              input: ({ event, context }) => {
                const orderData = context.orderData as any;
                const userInteraction =
                  orderData?.userRetailerCheckoutOrder
                    ?.userInteractionRequirement;

                return {
                  orderId: context.orderId,
                  communicationId: userInteraction?.communicationId,
                  interactionData: (event as any).data?.interactionData,
                };
              },
              onDone: {
                target: "retrying",
                actions: assign({
                  orderData: ({ event }) => event.output,
                  error: null,
                }),
              },
              onError: {
                target: "#orderTracking.error",
                actions: assign({
                  error: ({ event }) => event.error as Error,
                }),
              },
            },
          },
          retrying: {
            invoke: {
              id: "retryCheckout",
              src: retryCheckoutActor,
              input: ({ context, event }) => ({
                context: {
                  orderId: context.orderId,
                  // Add other context properties as needed for FastCheckoutMachineContext
                } as FastCheckoutMachineContext,
                event: event as FastCheckoutMachineEvents,
              }),
              onDone: {
                target: "#orderTracking.fetching",
                actions: assign({
                  error: null,
                  orderData: ({ event }) => event.output,
                  retryCount: 0,
                }),
              },
              onError: {
                target: "#orderTracking.error",
                actions: assign({
                  error: ({ event }) => event.error as Error,
                  retryCount: ({ context }) => context.retryCount + 1,
                }),
              },
            },
          },
        },
      },

      submittingInteraction: {
        invoke: {
          id: "submitUserInteraction",
          src: submitUserInteractionApi,
          input: ({ event, context }) => {
            const orderData = context.orderData as any;
            const userInteraction =
              orderData?.userRetailerCheckoutOrder?.userInteractionRequirement;

            return {
              orderId: context.orderId,
              communicationId: userInteraction?.communicationId,
              interactionData: (event as any).data?.interactionData,
            };
          },

          onDone: {
            target: "fetching", // Go back to fetching to get updated status
            actions: assign({
              error: null,
              orderData: ({ event }) => {
                return event.output;
              },
            }),
          },
          onError: {
            target: "userInteractionRequired", // Go back to interaction required state
            actions: assign({
              error: ({ event }) => event.error as Error,
            }),
          },
        },
      },

      completed: {
        on: {
          STOP_TRACKING: "idle",
          REFRESH: "fetching",
          START_TRACKING: "waiting",
        },
      },
      error: {
        after: {
          3000: {
            target: "fetching",
            guard: ({ context }) => context.retryCount < 3,
          },
        },
        on: {
          RETRY: "fetching",
          STOP_TRACKING: "idle",
        },
      },
    },
  });

// Custom hook for order tracking
export const useOrderTracking = (orderId: string) => {
  const [state, setState] = useState<any>({ value: "idle", context: {} });
  const actorRef = useRef<any>(null);

  useEffect(() => {
    if (!orderId) return;

    const machine = createOrderTrackingMachine(orderId);
    actorRef.current = createActor(machine);

    // Subscribe to state changes
    const subscription = actorRef.current.subscribe((snapshot: any) => {
      setState(snapshot);
    });

    // Start the actor
    actorRef.current.start();

    return () => {
      subscription.unsubscribe();
      actorRef.current?.stop();
    };
  }, [orderId]);

  const retryCheckout = () => {
    actorRef.current?.send({
      type: "RETRY_CHECKOUT",
    });
  };

  const startTracking = () =>
    actorRef.current?.send({ type: "START_TRACKING" });
  const stopTracking = () => actorRef.current?.send({ type: "STOP_TRACKING" });
  const refresh = () => actorRef.current?.send({ type: "REFRESH" });
  const retry = () => actorRef.current?.send({ type: "RETRY" });
  const fetchNow = () => actorRef.current?.send({ type: "FETCH_NOW" });
  const submitUserInteraction = (data: {
    interactionType: UserInteractionType;
    interactionData: UserRetailerCheckoutCommunicationResponse;
  }) => {
    actorRef.current?.send({
      type: "SUBMIT_USER_INTERACTION",
      data: { interactionData: data.interactionData },
    });
  };

  const submitUserInteractionAndRetry = (data: {
    interactionType: UserInteractionType;
    interactionData: UserRetailerCheckoutCommunicationResponse;
  }) => {
    actorRef.current?.send({
      type: "SUBMIT_USER_INTERACTION_AND_RETRY",
      data: { interactionData: data.interactionData },
    });
  };

  return {
    state: state.value,
    orderData: state.context.orderData,
    noConnection: state.context.error,

    isLoading: state.value === "fetching",
    isWaiting: state.value === "waiting",
    isCompleted: state.value === "completed",
    isUserInteractionRequired: state.value === "userInteractionRequired",
    isSubmittingInteraction:
      state.value === "submittingInteraction" ||
      (typeof state.value === "object" &&
        state.value.timeoutRetry === "submittingInteraction"),
    isTimeoutRetry:
      typeof state.value === "object" && "timeoutRetry" in state.value,
    isRetrying:
      typeof state.value === "object" &&
      state.value.timeoutRetry === "retrying",
    startTracking,
    retryCheckout,
    stopTracking,
    refresh,
    retry,
    fetchNow,
    submitUserInteraction,
    submitUserInteractionAndRetry,
  };
};
